import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

import { API_URL } from "../config";

const UserAuthContext = createContext(null);

export const useUserAuth = () => {
  const ctx = useContext(UserAuthContext);
  if (!ctx) throw new Error("useUserAuth must be used within a UserAuthProvider");
  return ctx;
};

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: When returning from Google OAuth (#session_id=...), skip the
    // /me check — AuthCallback will exchange the session_id first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/users/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API_URL}/api/users/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await axios.post(
      `${API_URL}/api/users/register`,
      payload,
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/users/logout`, {}, { withCredentials: true });
    } catch {
      /* noop */
    }
    setUser(null);
  };

  const updateProfile = async (patch) => {
    const { data } = await axios.patch(`${API_URL}/api/users/me`, patch, { withCredentials: true });
    setUser(data);
    return data;
  };

  return (
    <UserAuthContext.Provider
      value={{ user, loading, login, register, logout, updateProfile, refresh: checkAuth, setUser }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

/** Helper used by login/register forms to render FastAPI error details safely. */
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Algo correu mal. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
